/** @jsx React.createElement */
import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext.jsx';
import { apiGet } from '../utils/api';

const OnboardingContext = React.createContext();

export const useOnboarding = () => {
  const context = React.useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState('settings'); // 'settings', 'horaires', 'completed'
  const [loading, setLoading] = useState(true);

  // Vérifier si l'utilisateur a besoin d'onboarding
  const checkOnboardingStatus = async () => {
    if (!token || !user) {
      setNeedsOnboarding(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Vérifier si l'entreprise a des paramètres configurés
      const companyResponse = await apiGet('/api/entreprise/settings', { silent: true });
      
      // Vérifier si des horaires sont configurés
      const horaireResponse = await apiGet('/api/horaires');
      
      // Vérifier si l'entreprise est vraiment configurée (pas juste créée)
      const hasCompanySettings = companyResponse.success && 
        companyResponse.settings && 
        companyResponse.settings.nom_entreprise && 
        companyResponse.settings.secteur;
      
      // Vérifier si des horaires sont configurés (l'API retourne un planning)
      let hasHoraires = false;
      if (horaireResponse.success && horaireResponse.planning) {
        // Vérifier s'il y a au moins un horaire configuré dans le planning
        const planning = horaireResponse.planning;
        const jours = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
        hasHoraires = jours.some(jour => 
          planning[jour] && 
          (planning[jour].matin?.length > 0 || planning[jour].soir?.length > 0)
        );
      }
      
      console.log('🔍 Vérification onboarding:', {
        hasCompanySettings,
        hasHoraires,
        userActivationDate: user.date_activation,
        companyResponse: companyResponse.settings,
        horaireResponse: horaireResponse.planning
      });

      // Déterminer si l'utilisateur a besoin d'onboarding
      // Un utilisateur a besoin d'onboarding s'il a été activé récemment (dans les 30 jours)
      // ET qu'il n'a pas encore configuré son entreprise ou ses horaires
      const isNewlyActivated = user.date_activation && 
        (new Date() - new Date(user.date_activation)) < (30 * 24 * 60 * 60 * 1000); // 30 jours
      
      console.log('📊 Analyse onboarding:', {
        hasDateActivation: !!user.date_activation,
        dateActivation: user.date_activation,
        daysSinceActivation: user.date_activation ? Math.floor((new Date() - new Date(user.date_activation)) / (24 * 60 * 60 * 1000)) : null,
        isNewlyActivated,
        needsCompanySetup: !hasCompanySettings,
        needsHorairesSetup: !hasHoraires
      });
      
      if (isNewlyActivated && (!hasCompanySettings || !hasHoraires)) {
        setNeedsOnboarding(true);
        
        // Déterminer l'étape actuelle
        if (!hasCompanySettings) {
          setOnboardingStep('settings');
        } else if (!hasHoraires) {
          setOnboardingStep('horaires');
        } else {
          setOnboardingStep('completed');
          setNeedsOnboarding(false);
        }
      } else {
        setNeedsOnboarding(false);
        setOnboardingStep('completed');
      }
      
    } catch (error) {
      // En cas d'erreur, ne pas bloquer l'utilisateur
      setNeedsOnboarding(false);
      setOnboardingStep('completed');
    } finally {
      setLoading(false);
    }
  };

  // Marquer une étape comme complétée
  const completeStep = (step) => {
    
    if (step === 'settings' && onboardingStep === 'settings') {
      setOnboardingStep('horaires');
    } else if (step === 'horaires' && onboardingStep === 'horaires') {
      setOnboardingStep('completed');
      setNeedsOnboarding(false);
      
      // Sauvegarder dans localStorage que l'onboarding est terminé
      localStorage.setItem('onboarding_completed', 'true');
    }
  };

  // Ignorer l'onboarding (pour plus tard)
  const skipOnboarding = () => {
    console.log('⏭️ Onboarding ignoré');
    setNeedsOnboarding(false);
    setOnboardingStep('completed');
    localStorage.setItem('onboarding_skipped', 'true');
  };

  // Relancer la vérification
  const refreshOnboardingStatus = () => {
    checkOnboardingStatus();
  };

  // Vérifier l'état au chargement et quand l'utilisateur change
  useEffect(() => {
    if (user && token) {
      // Vérifier si l'onboarding a déjà été complété/ignoré
      const completed = localStorage.getItem('onboarding_completed');
      const skipped = localStorage.getItem('onboarding_skipped');
      
      if (completed || skipped) {
        setNeedsOnboarding(false);
        setOnboardingStep('completed');
        setLoading(false);
      } else {
        checkOnboardingStatus();
      }
    } else {
      setNeedsOnboarding(false);
      setLoading(false);
    }
  }, [user, token]);

  const value = {
    needsOnboarding,
    onboardingStep,
    loading,
    completeStep,
    skipOnboarding,
    refreshOnboardingStatus
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};




